if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      this.mql = window.matchMedia('(min-width: 750px)');

      if (this.elements.viewer) {
        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 50)
        );
      }

      if (this.elements.thumbnails) {
        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener(
                'click',
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
          });

        if (
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      this.syncInitialActiveState();
    }

    syncInitialActiveState() {
      if (!this.elements.viewer) return;

      const current =
        this.elements.viewer.querySelector(
          '.is-active[data-media-id]'
        ) ||
        this.elements.viewer.querySelector(
          '[data-media-id]'
        );

      if (current) {
        this.setActiveViewerSlide(current);
      }
    }

    onSlideChanged(event) {
      if (
        !event ||
        !event.detail ||
        !event.detail.currentElement
      ) {
        return;
      }

      this.setActiveViewerSlide(
        event.detail.currentElement
      );

      if (!this.elements.thumbnails) return;

      const thumbnail =
        this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );

      if (thumbnail) {
        this.setActiveThumbnail(thumbnail);
      }
    }

    setActiveViewerSlide(currentEl) {
      if (!this.elements.viewer || !currentEl) return;

      this.elements.viewer
        .querySelectorAll(
          '[data-media-id].is-active'
        )
        .forEach((el) => {
          el.classList.remove('is-active');
          el.removeAttribute('aria-current');
        });

      currentEl.classList.add('is-active');
      currentEl.setAttribute(
        'aria-current',
        'true'
      );
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia =
        this.elements.viewer.querySelector(
          `[data-media-id="${mediaId}"]`
        );

      this.elements.viewer
        .querySelectorAll('[data-media-id]')
        .forEach((element) => {
          element.classList.remove('is-active');
          element.removeAttribute('aria-current');
        });

      activeMedia.classList.add('is-active');
      activeMedia.setAttribute(
        'aria-current',
        'true'
      );

      if (prepend) {
        activeMedia.parentElement.prepend(
          activeMedia
        );

        if (this.elements.thumbnails) {
          const activeThumbnail =
            this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );

          activeThumbnail.parentElement.prepend(
            activeThumbnail
          );
        }

        if (this.elements.viewer.slider) {
          this.elements.viewer.resetPages();
        }
      }

      this.preventStickyHeader();

      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({
            left: activeMedia.offsetLeft
          });
        }

        if (
          !this.elements.thumbnails ||
          this.dataset.desktopLayout === 'stacked'
        ) {
          activeMedia.scrollIntoView({
            behavior: 'smooth'
          });
        }
      });

      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;

      const activeThumbnail =
        this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );

      this.setActiveThumbnail(
        activeThumbnail
      );

      this.announceLiveRegion(
        activeMedia,
        activeThumbnail.dataset.mediaPosition
      );
    }

    setActiveThumbnail(thumbnail) {
      if (
        !this.elements.thumbnails ||
        !thumbnail
      ) {
        return;
      }

      this.elements.thumbnails
        .querySelectorAll('button')
        .forEach((element) => {
          element.removeAttribute(
            'aria-current'
          );
        });

      thumbnail
        .querySelector('button')
        .setAttribute(
          'aria-current',
          true
        );

      this.elements.thumbnails
        .querySelectorAll('.thumbnail')
        .forEach((element) => {
          element.classList.remove(
            'is-active'
          );
        });

      thumbnail
        .querySelector('.thumbnail')
        .classList.add('is-active');

      const thumbnailSlider =
        this.elements.thumbnails.slider ||
        this.elements.thumbnails.querySelector(
          'ul.slider'
        );

      if (!thumbnailSlider) return;

      /*
       * Mobile SOLMIELATO:
       * - 5 miniature visibili
       * - nessuna freccia
       * - swipe nativo
       * - clic sull’ultima miniatura visibile:
       *   la barra avanza mostrando le successive.
       */
      if (!this.mql.matches) {
        const items = [
          ...thumbnailSlider.querySelectorAll(
            'li[data-target], li.slider-slide'
          )
        ];

        const index =
          items.indexOf(thumbnail);

        const itemWidth =
          thumbnail.offsetWidth ||
          items[0]?.offsetWidth ||
          0;

        const computedStyle =
          window.getComputedStyle(
            thumbnailSlider
          );

        const gap =
          parseFloat(
            computedStyle.columnGap
          ) ||
          parseFloat(
            computedStyle.gap
          ) ||
          0;

        const step =
          itemWidth + gap;

        const currentLeft =
          thumbnailSlider.scrollLeft;

        const visibleRight =
          currentLeft +
          thumbnailSlider.clientWidth;

        const thumbLeft =
          thumbnail.offsetLeft;

        const thumbRight =
          thumbLeft +
          thumbnail.offsetWidth;

        const isAtRightEdge =
          thumbRight >=
          visibleRight -
            step * 0.35;

        const isAtLeftEdge =
          thumbLeft <=
          currentLeft +
            step * 0.35;

        if (
          isAtRightEdge &&
          index < items.length - 1
        ) {
          const targetIndex =
            Math.min(
              items.length - 1,
              index + 2
            );

          const targetLeft =
            Math.max(
              0,
              items[targetIndex].offsetLeft -
                thumbnailSlider.clientWidth +
                step
            );

          thumbnailSlider.scrollTo({
            left: targetLeft,
            behavior: 'smooth'
          });

          return;
        }

        if (
          isAtLeftEdge &&
          index > 0
        ) {
          const targetIndex =
            Math.max(
              0,
              index - 2
            );

          thumbnailSlider.scrollTo({
            left:
              items[targetIndex].offsetLeft,
            behavior: 'smooth'
          });

          return;
        }

        if (
          thumbLeft < currentLeft ||
          thumbRight > visibleRight
        ) {
          thumbnailSlider.scrollTo({
            left: Math.max(
              0,
              thumbLeft -
                (
                  thumbnailSlider.clientWidth -
                  thumbnail.offsetWidth
                ) / 2
            ),
            behavior: 'smooth'
          });
        }

        return;
      }

      if (
        typeof
          this.elements.thumbnails
            .isSlideVisible ===
          'function' &&
        this.elements.thumbnails
          .isSlideVisible(
            thumbnail,
            10
          )
      ) {
        return;
      }

      thumbnailSlider.scrollTo({
        left: thumbnail.offsetLeft
      });
    }

    announceLiveRegion(
      activeItem,
      position
    ) {
      const image =
        activeItem.querySelector(
          '.product__modal-opener--image img'
        );

      if (!image) return;

      image.onload = () => {
        this.elements.liveRegion
          .setAttribute(
            'aria-hidden',
            false
          );

        this.elements.liveRegion
          .innerHTML =
            window.accessibilityStrings
              .imageAvailable
              .replace(
                '[index]',
                position
              );

        setTimeout(() => {
          this.elements.liveRegion
            .setAttribute(
              'aria-hidden',
              true
            );
        }, 2000);
      };

      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();

      const deferredMedia =
        activeItem.querySelector(
          '.deferred-media'
        );

      if (deferredMedia) {
        deferredMedia.loadContent(false);
      }
    }

    preventStickyHeader() {
      this.stickyHeader =
        this.stickyHeader ||
        document.querySelector(
          'sticky-header'
        );

      if (!this.stickyHeader) return;

      this.stickyHeader.dispatchEvent(
        new Event(
          'preventHeaderReveal'
        )
      );
    }

    removeListSemantic() {
      if (
        !this.elements.viewer.slider
      ) {
        return;
      }

      this.elements.viewer.slider
        .setAttribute(
          'role',
          'presentation'
        );

      this.elements.viewer.sliderItems
        .forEach((slide) => {
          slide.setAttribute(
            'role',
            'presentation'
          );
        });
    }
  });
}

if (
  !customElements.get(
    'vertical-thumbnail-slider'
  )
) {
  class VerticalThumbnailSlider
    extends HTMLElement {
    constructor() {
      super();

      this.slider =
        this.querySelector(
          'ul.slider'
        );

      this.sliderItems =
        this.querySelectorAll(
          'li.slider-slide'
        );

      this.prevButton =
        this.querySelector(
          'button[name="previous"]'
        );

      this.nextButton =
        this.querySelector(
          'button[name="next"]'
        );

      this.step = parseInt(
        this.prevButton?.dataset.step ||
          '1',
        10
      );

      this.sliderItemHeight = 0;

      if (
        !this.slider ||
        !this.nextButton
      ) {
        return;
      }

      this.init();

      this.prevButton.addEventListener(
        'click',
        this.scrollUp.bind(this)
      );

      this.nextButton.addEventListener(
        'click',
        this.scrollDown.bind(this)
      );

      this.slider.addEventListener(
        'scroll',
        this.updateButtons.bind(this)
      );

      const resizeObserver =
        new ResizeObserver(() => {
          this.init();
        });

      resizeObserver.observe(
        this.slider
      );
    }

    init() {
      this.sliderItems =
        this.querySelectorAll(
          'li.slider-slide'
        );

      if (
        this.sliderItems.length < 2
      ) {
        return;
      }

      this.sliderItemHeight =
        this.sliderItems[1].offsetTop -
        this.sliderItems[0].offsetTop;

      this.updateButtons();
    }

    scrollUp(event) {
      event.preventDefault();

      this.slider.scrollBy({
        top:
          -this.step *
          this.sliderItemHeight,
        behavior: 'smooth'
      });
    }

    scrollDown(event) {
      event.preventDefault();

      this.slider.scrollBy({
        top:
          this.step *
          this.sliderItemHeight,
        behavior: 'smooth'
      });
    }

    updateButtons() {
      if (!this.slider) return;

      const scrollTop =
        this.slider.scrollTop;

      const maxScrollTop =
        this.slider.scrollHeight -
        this.slider.clientHeight;

      if (scrollTop <= 0) {
        this.prevButton.style.display =
          'none';
      } else {
        this.prevButton.style.display =
          'flex';
      }

      if (
        scrollTop >=
        maxScrollTop - 1
      ) {
        this.nextButton.style.display =
          'none';
      } else {
        this.nextButton.style.display =
          'flex';
      }
    }

    isSlideVisible(
      element,
      offset = 0
    ) {
      const lastVisibleSlide =
        this.slider.scrollTop +
        this.slider.clientHeight -
        offset;

      return (
        element.offsetTop +
          element.clientHeight <=
          lastVisibleSlide &&
        element.offsetTop >=
          this.slider.scrollTop
      );
    }
  }

  customElements.define(
    'vertical-thumbnail-slider',
    VerticalThumbnailSlider
  );
}